import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  CircularProgress,
  Card,
  CardContent,
  Pagination,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { useAppDispatch, useAppSelector } from '../hooks/redux';
import { 
  fetchProductMasterItems,
  addProductMasterItem, 
  updateProductMasterItem, 
  deleteProductMasterItem,
  clearError 
} from '../store/slices/productMasterSlice';
import { toast } from 'react-toastify';
import DeleteConfirmDialog from '../components/dialogs/DeleteConfirmDialog';

const ProductMaster: React.FC = () => {
  const dispatch = useAppDispatch();
  const { items, loading, error, pagination } = useAppSelector((state: any) => state.productMaster);
  
  const [labelName, setLabelName] = useState('');
  const [labelError, setLabelError] = useState<string>('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<{ id: string; lable: string; data: string } | null>(null);
  const [currentPage, setCurrentPage] = useState(1);

  // Fetch data on component mount
  useEffect(() => {
    dispatch(fetchProductMasterItems({ skip: 0, limit: 10 }));
  }, [dispatch]);

  useEffect(() => {
    if (error) {
      toast.error(error);
      dispatch(clearError());
    }
  }, [error, dispatch]);

  const handleAddLabel = async () => {

    // Replace multiple spaces with a single space before validation
    let value = labelName.replace(/\s+/g, ' ').trim();
    if (!value) {
      setLabelError('Label name is required');
      return;
    }
    if (value.length < 3) {
      setLabelError('Name must be at least 3 characters');
      return;
    }
    if (value.length > 50) {
      setLabelError('Name must be less than 50 characters');
      return;
    }
    // Only alphabets and single spaces between words
    if (!/^([A-Za-z]+)( [A-Za-z]+)*$/.test(value)) {
      setLabelError('Label name can only contain alphabets and a single space between words (e.g., Hello World)');
      return;
    }
    setLabelError('');

    try {
      await dispatch(addProductMasterItem(value)).unwrap();
      setLabelName('');
      toast.success('Label added successfully!');
      // Refresh the list
      dispatch(fetchProductMasterItems({ skip: 0, limit: 10 }));
    } catch (error) {
      // Error is handled by the slice
    }
  };

  const handleEdit = (item: { id: string; lable: string; data: string }) => {
    setEditingId(item.id);
    setEditName(item.lable);
  };

  const handleUpdate = async () => {
    if (!editingId || !editName.trim()) {
      toast.error('Please enter a valid label name');
      return;
    }

    try {
      await dispatch(updateProductMasterItem({ id: editingId, label: editName.trim() })).unwrap();
      setEditingId(null);
      setEditName('');
      toast.success('Label updated successfully!');
    } catch (error) {
      // Error is handled by the slice
    }
  };

  const handleDelete = (item: { id: string; lable: string; data: string }) => {
    setItemToDelete(item);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (itemToDelete) {
      try {
        await dispatch(deleteProductMasterItem(itemToDelete.id)).unwrap();
        setDeleteDialogOpen(false);
        setItemToDelete(null);
        toast.success('Label deleted successfully!');
        // Refresh the list
        dispatch(fetchProductMasterItems({ skip: 0, limit: 10 }));
      } catch (error) {
        // Error is handled by the slice
      }
    }
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditName('');
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      handleAddLabel();
    }
  };

  const handlePageChange = (event: React.ChangeEvent<unknown>, page: number) => {
    setCurrentPage(page);
    const skip = (page - 1) * pagination.limit;
    dispatch(fetchProductMasterItems({ skip, limit: pagination.limit }));
  };

  const totalPages = Math.ceil(pagination.total / pagination.limit);

  return (
    <Box>
      <Typography variant="h5" gutterBottom sx={{ mb: 4, fontWeight: 800, fontSize: '1.1rem', letterSpacing: .4 }}>
        Product Master
      </Typography>

      <Box sx={{ display: 'flex', gap: 3, flexDirection: { xs: 'column', md: 'row' } }}>
        {/* Left Side - Add Form */}
        <Box sx={{ width: { xs: '100%', md: '33%' } }}>
          <Card sx={{ height: 'fit-content' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ mb: 3, fontWeight: 800, fontSize: '.95rem', letterSpacing: .5, color: 'primary.main' }}>
                Add Product Details Label
              </Typography>
              
              <TextField
                fullWidth
                label="Label name *"
                InputLabelProps={{ sx: { fontSize: '.7rem', textTransform: 'uppercase', letterSpacing: .6, fontWeight: 600 } }}
                value={labelName}
                onChange={(e) => {
                  setLabelName(e.target.value);
                  setLabelError('');
                }}
                onKeyPress={handleKeyPress}
                placeholder="Enter label name"
                sx={{ mb: 3 }}
                disabled={loading}
                error={!!labelError}
                helperText={labelError}
              />
              
              <Button
                variant="contained"
                startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <AddIcon />}
                onClick={handleAddLabel}
                disabled={loading || !labelName.trim()}
                fullWidth
                sx={{
                  py: 1.2,
                  bgcolor: 'secondary.main',
                  '&:hover': {
                    bgcolor: 'primary.main',
                  },
                  textTransform: 'none',
                  fontSize: '.8rem',
                  fontWeight: 600,
                }}
              >
                {loading ? 'Adding...' : 'Add Label'}
              </Button>
            </CardContent>
          </Card>
        </Box>

        {/* Right Side - Listing */}
        <Box sx={{ flex: 1 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ mb: 3, fontWeight: 800, fontSize: '.95rem', letterSpacing: .5, color: 'primary.main' }}>
                Product Details Labels Listing
              </Typography>
              
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '.7rem', letterSpacing: .6, textTransform: 'uppercase' }}>
                        Name
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '.7rem', letterSpacing: .6, textTransform: 'uppercase' }}>
                        Action
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {items.map((item: { id: string; lable: string; data: string }) => (
                      <TableRow key={item.id} hover>
                        <TableCell>
                          {editingId === item.id ? (
                            <TextField
                              value={editName}
                              onChange={(e) => setEditName(e.target.value)}
                              size="small"
                              fullWidth
                              onKeyPress={(e) => {
                                if (e.key === 'Enter') {
                                  handleUpdate();
                                }
                              }}
                            />
                          ) : (
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {item.lable}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {editingId === item.id ? (
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              <IconButton
                                size="small"
                                onClick={handleUpdate}
                                disabled={loading}
                                sx={{ 
                                  color: 'primary.main',
                                  '&:hover': { backgroundColor: 'primary.light' }
                                }}
                              >
                                <AddIcon />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={handleCancelEdit}
                                disabled={loading}
                                sx={{ 
                                  color: 'error.main',
                                  '&:hover': { backgroundColor: 'error.light' }
                                }}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Box>
                          ) : (
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              <IconButton
                                size="small"
                                onClick={() => handleEdit(item)}
                                disabled={loading}
                                sx={{ 
                                  color: 'primary.main',
                                  '&:hover': { backgroundColor: 'primary.light' }
                                }}
                              >
                                <EditIcon />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => handleDelete(item)}
                                disabled={loading}
                                sx={{ 
                                  color: 'error.main',
                                  '&:hover': { backgroundColor: 'error.light' }
                                }}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Box>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              
              {items.length === 0 && !loading && (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                    No labels found. Add your first label using the form on the left.
                  </Typography>
                </Box>
              )}

              {/* Pagination */}
              {totalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                  <Pagination
                    count={totalPages}
                    page={currentPage}
                    onChange={handlePageChange}
                    color="primary"
                    showFirstButton
                    showLastButton
                  />
                </Box>
              )}
            </CardContent>
          </Card>
        </Box>
      </Box>
      {/* Delete Confirmation Dialog */}
      <DeleteConfirmDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleConfirmDelete}
        loading={loading}
        title="Delete Label"
        itemName={itemToDelete?.lable || ''}
        itemType="label"
      />
    </Box>
  );
};

export default ProductMaster;